import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { headerRatingBus } from 'pages/ahu-board/bus/ahuBoardBus';
import { FanSelector } from 'pages/ahu-board/forms/selector/FanSelector';
import { CoilSelector } from 'pages/ahu-board/forms/selector/CoilSelector';
import { EnergyWheelSelector } from 'pages/ahu-board/forms/selector/EnergyWheelSelector';
import { PlateHeatExchangerSelector } from 'pages/ahu-board/forms/selector/PlateHeatExchangerSelector';
import { ParkedVendorPlaceholder } from 'pages/ahu-board/forms/selector/ParkedVendorPlaceholder';
import { useRatingNumberKeypress } from 'pages/ahu-board/forms/selector/useRatingNumberKeypress';
import type { SelectorTabProps } from 'pages/ahu-board/forms/selector/selectorTypes';
import { setActiveTab, setPopupState } from './independentRatingStorage';

/**
 * Phase 6.5 — the HEADER "Independent Rating" popup
 * (`#component_selector_button`).
 *
 * React port of the TWO legacy Backbone views that make up the header host:
 *   - `component_selector/view/component_selector.popup.js` (64) +
 *     `tmpl/component_selector.popup.tmpl` (8)  — the `.popup-wrapper` shell
 *     and the CLOSE button.
 *   - `component_selector/view/component_selector.js` (538) +
 *     `tmpl/component_selector.tmpl` (156), the `show_tab:'all'` branch — the
 *     tab strip, `#rate`/`#print`, the minimize icon, the 7 content divs and the
 *     bottom-right note.
 *
 * ★ IT IS A CONTEXT-FREE RATING SANDBOX. `new ComponentSelectorView({header:
 * true})` (popup.js:39) passes nothing else, so `ahu`/`project`/`section`/
 * `current_model` are undefined for the whole lifetime — there is NO project,
 * AHU, section or tunnel picker anywhere (verified four ways in discovery §0),
 * and the UI says so: `* Rate Components independent of Projects and Air
 * Handling Units.` (component_selector.tmpl:138).
 *
 * ★ ZERO WRITES. `.control-buttons` (`#select` + `#cancel`) is wrapped in
 * `{{#ifCompare show_tab '!=' 'all'}}` (tmpl:131-136) so it is NEVER rendered;
 * `#reset` is only emitted for the single-tab variants (tmpl:62-76); `#notes`
 * exists nowhere in the codebase. The ONLY network mutation the whole feature
 * can issue is the vendor dispatch `POST /api/v1.0/rating/` on an explicit
 * `#rate` click (discovery §3).
 *
 * ★ CREATE-ONCE, NEVER DESTROY (header.js:525-528 + rating_mode.scss:582-591):
 * the popup is constructed on the first open and only ever gains/loses
 * `.active`; CSS keeps it `display:block !important` with `z-index:-1;opacity:0`
 * when inactive. Every rendered selector body and the active tab therefore
 * survive close→reopen. The React host (useIndependentRating) keeps this
 * component mounted forever once opened, and visited tabs stay mounted behind
 * `.hide` — legacy's `renderComponentRatingMode` is show/hide + lazy FIRST
 * construction, never unmount (component_selector.js:126-186; live evidence:
 * 0 requests when returning to an already-visited tab).
 *
 * DELIBERATELY NOT PORTED (dead in header mode — discovery §7.2):
 *   selectComponent (:259) · cancel (:265) · reset (:241) · showNotes (:272) ·
 *   saveChanges (:187) · rotateCanvas (:198) · hideRatingDialogBox (:219) ·
 *   renderFromAHU (:59) · makeChanges (:530).
 * ★ `rotateCanvas` in particular is bound to `click button.close` INSIDE the
 * chassis element, while the only `button.close` lives in the popup's own
 * `.popup-control-buttons` — a SIBLING of `.popup-content-part`, i.e. outside
 * the chassis's delegation root. That is precisely why it is dead. The CLOSE
 * button below is rendered in the same place for the same reason: re-parenting
 * it into the chassis body would make a header-popup close rotate the AHU
 * board's canvas (discovery §7.3 trap 2).
 *
 * The popup is NOT draggable (live `ui-draggable` count 0) and has no Esc /
 * backdrop close (see the `.component-selector-popup` exemption in
 * hooks/usePopupKeys.ts).
 */

/** component_selector.tmpl:5-11 — the tab strip, VERBATIM order + labels.
 * Three entries sit on `<li class="hide">`: they are fully wired but no UI path
 * reaches them, and they are kept in the DOM for exact markup parity
 * (orchestrator decision 4). */
const HEADER_TABS: Array<{ value: string; label: string; hidden?: boolean }> = [
  { value: 'fan', label: 'Fan' },
  { value: 'coil', label: 'Coil' },
  { value: 'gas_heater', label: 'GAS HEATER', hidden: true },
  { value: 'energy_wheel', label: 'HEAT WHEEL' },
  { value: 'plate_heat_exchanger', label: 'PLATE HEAT EXCHANGER' },
  { value: 'uvc', label: 'UV-C', hidden: true },
  { value: 'filter', label: 'FILTER', hidden: true },
];

/** component_selector.tmpl:81-87 — the 7 content divs. NOTE the order differs
 * from the tab strip (uvc precedes energy_wheel here); kept verbatim. */
const HEADER_CONTENT_ORDER = [
  'fan',
  'coil',
  'gas_heater',
  'uvc',
  'energy_wheel',
  'plate_heat_exchanger',
  'filter',
];

/** component_selector.js:175/183 + rating_mode.scss → common.css:12593-12596:
 * `.ahu-rate-content.hide-print #print { opacity:0; pointer-events:none }`.
 * Print is live for fan + coil ONLY. */
const HIDE_PRINT_TABS = new Set(['plate_heat_exchanger', 'energy_wheel']);

/**
 * ★ THE BUILDER-2 SEAM — the header host's selector registry.
 *
 * Every entry receives the SAME `SelectorTabProps` contract the board chassis
 * uses, with the Phase 6.5 header seams set (see selectorTypes.ts):
 *   modelId: 0                → no current_model; `!modelId` guards bail exactly
 *                               like legacy's `if (!fan_id) return`
 *   ahu: null / activate:{name}→ no AHU / project / section context at all
 *   headerMode: true          → standalone localStorage seed + AHU guards + no
 *                               persist seams + no `:selector:select` subscription
 *   eventScope: 'header'      → the fan-out key is `<tab>_header:selector:<action>`
 *   bus: headerRatingBus      → isolated from the board bus
 *   setSelectEnabled: no-op   → `#select` is never rendered here
 *   setPrintEnabled           → drives `#print.non-clickable`
 *
 * ALL FOUR user-reachable tabs are now wired (Phase 6.5 builder 2 added coil /
 * energy_wheel / plate_heat_exchanger), each with the same `headerMode`
 * standalone seed FanSelector has, off its own `<x>_independent_ratings` key
 * (see RATING_STORAGE_KEYS). The three `li.hide` tabs (gas_heater / uvc /
 * filter) have no UI path and keep the ParkedVendorPlaceholder fallback below.
 */
const HEADER_SELECTORS: Record<string, (props: SelectorTabProps) => ReactElement> = {
  fan: (props) => <FanSelector {...props} />,
  // coil_independent_ratings + the coilDefaults `options.header` override
  // (coil.selector.js:394-420). Print is LIVE (no `hide-print`).
  coil: (props) => <CoilSelector {...props} />,
  // rotary_independent_ratings (NOT energy_wheel_… — energy_wheel.selector.js
  // :1386). tunnel_configuration → the literal 'vertical' (js:2547). Print
  // SUPPRESSED via `hide-print` (component_selector.js:175/:183).
  energy_wheel: (props) => <EnergyWheelSelector {...props} />,
  // plate_heat_exchanger_independent_ratings. Print SUPPRESSED (`hide-print`).
  plate_heat_exchanger: (props) => <PlateHeatExchangerSelector {...props} />,
};

interface IndependentRatingPopupProps {
  /** the `.active` class (header.js:525-531 / popup.js:49). */
  active: boolean;
  /** popup.js:52-58 — the CLOSE button. */
  onClose: () => void;
  /** component_selector.js:284-289 — the minimize icon. */
  onMinimize: () => void;
  /** component_selector.popup.js:42 — `localStorage.active_independent_rating
   * || 'fan'`, read ONCE at construction. */
  initialTab: string;
}

const noop = () => undefined;

export const IndependentRatingPopup = ({
  active,
  onClose,
  onMinimize,
  initialTab,
}: IndependentRatingPopupProps) => {
  const [tab, setTab] = useState(initialTab);
  /** lazily-constructed tab bodies (component_selector.js:143 — a selector view
   * is only constructed when its content div is still empty; once constructed it
   * is never torn down). */
  const [visited, setVisited] = useState<string[]>(() => [initialTab]);
  /** per-tab `#print` availability, reported by the selector
   * (checkButtonsAvailability → `#print` toggle). */
  const [printEnabled, setPrintEnabled] = useState<Record<string, boolean>>({});
  const printSetters = useRef<Record<string, (enabled: boolean) => void>>({});

  // component_selector.js:33 — the chassis's number-input character filter.
  // The header host is the same MainView, so the same delegated keypress
  // binding covers this popup; scope it to the popup's own root (the board's
  // `#component_rating_mode` is a disjoint subtree).
  useRatingNumberKeypress('.component-selector-popup');

  // component_selector.js:103-113 renderPopupContent — runs ONCE per
  // construction: renders with show_tab:'all', renders the active tab, and sets
  // `independent_rating_popup_state = 'open'` (:111). Because the popup is never
  // re-constructed, this is also the ONLY site that writes 'open' — which is the
  // root of the known state desync (see useIndependentRating).
  useEffect(() => {
    setActiveTab(initialTab);
    setPopupState('open');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** component_selector.js:115-186 renderComponentRatingMode. */
  const activateTab = useCallback((name: string) => {
    setTab(name);
    // :128-129 — written on EVERY tab render, with the 'all' → 'fan' fold.
    setActiveTab(name);
    setVisited((prev) => (prev.includes(name) ? prev : [...prev, name]));
  }, []);

  /** component_selector.js:278-282 rate → `<render>_header:selector:rate`. */
  const onRate = useCallback(() => {
    headerRatingBus.emit(`${tab}_header:selector:rate`);
  }, [tab]);

  /** component_selector.js:247-257 print — clears the body zoom, dispatches,
   * restores the zoom 300 ms later. */
  const onPrint = useCallback(() => {
    if (!printEnabled[tab]) return; // `.non-clickable` blocks the real click too
    const originalZoom = document.body.style.zoom;
    document.body.style.zoom = '';
    headerRatingBus.emit(`${tab}_header:selector:print`);
    window.setTimeout(() => {
      document.body.style.zoom = originalZoom;
    }, 300);
  }, [tab, printEnabled]);

  const printSetterFor = useCallback((name: string) => {
    let setter = printSetters.current[name];
    if (!setter) {
      setter = (enabled: boolean) =>
        setPrintEnabled((prev) => (prev[name] === enabled ? prev : { ...prev, [name]: enabled }));
      printSetters.current[name] = setter;
    }
    return setter;
  }, []);

  // The header activation payload is `{name}` and NOTHING else (there is no
  // ahu/project/section/current_model to pass — component_selector.js:103-113).
  // Cached per tab so its IDENTITY is stable: several selector callbacks
  // (collectData → doRate → the §30.4 subscription effect) list `activate` in
  // their dep arrays, and a fresh object each render would re-subscribe the bus
  // on every render.
  const activates = useRef<Record<string, { name: string }>>({});
  const activateFor = useCallback((name: string) => {
    let a = activates.current[name];
    if (!a) {
      a = { name };
      activates.current[name] = a;
    }
    return a;
  }, []);

  const hidePrint = HIDE_PRINT_TABS.has(tab);

  return (
    /* popup.js:21 className — `.component-selector-popup` supplies the
       display:block/z-index/opacity state machine and the 1300×calc geometry
       from common.css:12563-12582; `.no-rotation` and `.popup-wrapper` are
       legacy too. NO id (legacy has none). */
    <div className={`popup-wrapper component-selector-popup no-rotation${active ? ' active' : ''}`}>
      <div className="popup-content">
        {/* popup.tmpl:3 — the chassis mounts INSIDE .popup-content-part … */}
        <div className="popup-content-part">
          {/* … as the chassis Backbone view's own bare <div> el (no className:
              MainView declares neither tagName nor className, so Backbone
              creates a plain div). Keeping it preserves the CSS descendant
              depth the legacy selectors were written against. */}
          <div>
            <div
              className={`ahu-rate-content content-with-pagers all${hidePrint ? ' hide-print' : ''}`}
            >
              <ul className="clearfix list-inline content-pager all">
                {HEADER_TABS.map((t) => {
                  // tmpl:5-11 emits `<li>` or `<li class="hide">`;
                  // renderComponentRatingMode (:126-127) then strips `active`
                  // from every li and adds it to the matching one. A hidden tab
                  // that is somehow active therefore reads `hide active`.
                  const cls = [t.hidden ? 'hide' : null, tab === t.value ? 'active' : null]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <li key={t.value} className={cls || undefined}>
                      {/* legacy renders a bare <a value=… name="rating_mode_button">
                          with no href; the chassis delegates the click. */}
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a
                        value={t.value}
                        name="rating_mode_button"
                        onClick={() => activateTab(t.value)}
                      >
                        {t.label}
                      </a>
                    </li>
                  );
                })}

                {/* tmpl:54-77 — the control-header buttons. In the 'all' variant
                    ONLY the minimize icon, #rate and #print are emitted (#reset
                    is gated on the single-tab variants). */}
                <li className="control-header-buttons independent-rating-control-header-buttons">
                  <div
                    className="right col-xs-1 minimize-icon header__icon independent_rating_minimize_icon"
                    onClick={onMinimize}
                  >
                    <i className="right icon-remove " />
                  </div>
                  {/* className is INTENTIONALLY constant (`all-rate` — legacy
                      `{{show_tab}}-rate` with show_tab:'all'): the mounted
                      selector owns `#rate.non-clickable` imperatively via its
                      checkRequiredFields effect, so React must never re-patch
                      this attribute. Only data-type changes per tab (:173/:181). */}
                  <button
                    id="rate"
                    className="right btn-add all-rate"
                    tabIndex={-1}
                    data-type={tab}
                    onClick={onRate}
                  >
                    rate
                  </button>
                  <button
                    id="print"
                    className={`print-btn icon-print${printEnabled[tab] ? '' : ' non-clickable'}`}
                    onClick={onPrint}
                  />
                </li>
              </ul>

              <div className="ahu-rate-content-item ahu-rate-content-item-with-subtabs rate-items independent-rating-content">
                {HEADER_CONTENT_ORDER.map((name) => {
                  const isActive = tab === name;
                  const isVisited = visited.includes(name);
                  const render = HEADER_SELECTORS[name];
                  return (
                    <div
                      key={name}
                      id={`rating_mode_${name}_content`}
                      className={`rating_mode_all_element${isActive ? '' : ' hide'}`}
                    >
                      {isVisited &&
                        (render
                          ? render({
                              modelId: 0,
                              ahu: null,
                              activate: activateFor(name),
                              headerMode: true,
                              eventScope: 'header',
                              bus: headerRatingBus,
                              setSelectEnabled: noop,
                              showNotes: noop,
                              setPrintEnabled: printSetterFor(name),
                            })
                          : (
                            <ParkedVendorPlaceholder
                              label={
                                HEADER_TABS.find((t) => t.value === name)?.label ?? name
                              }
                            />
                          ))}
                    </div>
                  );
                })}
              </div>

              {/* tmpl:137-139 — the 'all'-only note. THIS is the UI's own
                  statement that there is no project/AHU picker. */}
              <div className="all-rating-note">
                * Rate Components independent of Projects and Air Handling Units.
              </div>

              {/* tmpl:141-154 — emitted in header mode too, but it only ever
                  gains `.active` from renderFromAHU's BOARD branch (:74), so it
                  is permanently `display:none !important` here
                  (_ui.popups.scss:14-15). Rendered for DOM parity; its
                  save/close buttons are inert (saveChanges is dead — §7.2). */}
              <div className="popup-wrapper" id="save_changes_popup">
                <div className="popup-content">
                  <h2 className="popup-heading">Save changes?</h2>
                  <div className="clearfix popup-content-part">
                    <p>If you choose to Close, all changes will be lost.</p>
                  </div>
                  <div className="popup-control-buttons">
                    <button
                      className="save_changes_btn not-highlighted-btn"
                      name="save_changes_btn"
                      data-for="#select"
                    >
                      Save
                    </button>
                    <button
                      className="default-btn highlighted-btn cancel_changes_btn"
                      name="cancel_changes_btn"
                      data-for="#cancel"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* popup.tmpl:4-6 — ★ OUTSIDE .popup-content-part (i.e. outside the
            chassis subtree) ON PURPOSE. See the rotateCanvas note in the file
            header: nesting this inside the chassis is exactly the bleed
            discovery §7.3 trap 2 warns about. The `close_btn` name is also load
            bearing — it is what keeps the popup out of every Esc-cancel
            selector (see hooks/usePopupKeys.ts). */}
        <div className="popup-control-buttons">
          <button
            id="close"
            className="default-btn close-btn close"
            name="close_btn"
            onClick={onClose}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndependentRatingPopup;
